import { useEffect, useRef } from "react";
import { clsx } from "clsx";
import { IconRefresh } from "@tabler/icons-react";
import type { ChatMessage } from "../../../ai-search/data/types/chat";
import { parseChatResponse } from "../../../ai-search/utils/parse-chat-response";
import { ChatMarkdown } from "../../../ai-search/presentation/components/chat/chat-markdown";
import { WidgetRenderer } from "../../../ai-search/presentation/components/chat/widget-renderer";
import { TypingIndicator } from "../../../ai-search/presentation/components/chat/typing-indicator";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";

interface LandingChatTranscriptProps {
  transcript: ChatMessage[];
  responding: boolean;
  onRetry?: (() => void) | null;
  onSendMessage?: (text: string, productId?: number) => void;
}

// The first turn (index 0 user + index 1 assistant) is visualised by the
// recommendations panel above, so the transcript starts rendering from
// message[2] onwards. If there aren't any follow-ups yet, the component
// renders nothing.
export function LandingChatTranscript({ transcript, responding, onRetry, onSendMessage }: LandingChatTranscriptProps) {
  const followUps = transcript;
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  // Auto-follow is driven by *user intent*, not scroll position. We listen
  // for wheel + touchmove (which `scrollIntoView` never fires) so programmatic
  // scrolls and manual scrolls can't fight each other. Once the user wheels /
  // drags up, auto-follow stays off until they explicitly send the next
  // message — then we force it back on so the new bubble is revealed.
  const shouldAutoFollow = useRef(true);
  const lastUserScrollAt = useRef(0);
  const prevCount = useRef(0);
  const prevLastText = useRef("");

  const getScroller = (): HTMLElement | null => {
    let el: HTMLElement | null = scrollAnchorRef.current;
    while (el && el.id !== "rpPanel") el = el.parentElement;
    return el;
  };

  useEffect(() => {
    const scroller = getScroller();
    if (!scroller) return;
    const onUserIntent = () => {
      lastUserScrollAt.current = performance.now();
      const distance = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      // Re-engage only when they come all the way back to the bottom.
      shouldAutoFollow.current = distance < 60;
    };
    scroller.addEventListener("wheel", onUserIntent, { passive: true });
    scroller.addEventListener("touchmove", onUserIntent, { passive: true });
    return () => {
      scroller.removeEventListener("wheel", onUserIntent);
      scroller.removeEventListener("touchmove", onUserIntent);
    };
  }, []);

  useEffect(() => {
    const count = followUps.length;
    const last = followUps.at(-1);
    const lastText = last?.text ?? "";
    const isNew = count > prevCount.current;
    const isGrowing = count === prevCount.current && lastText !== prevLastText.current;

    // On send, force re-engage auto-follow — the new user bubble (and the
    // reply that follows it) should come into view even if the user had
    // scrolled up to read earlier context. The user message and its
    // placeholder assistant reply are appended together in one state update
    // (see useChat.ts sendMessage), so the array's last element is always the
    // assistant placeholder — check every newly added message, not just the last one.
    const newlyAdded = isNew ? followUps.slice(prevCount.current) : [];
    if (newlyAdded.some((m) => m.role === "user")) {
      shouldAutoFollow.current = true;
      lastUserScrollAt.current = 0;
    }

    // Respect a fresh user scroll for a short grace period. Without this, a
    // delta that lands right after the user wheels up would still trigger a
    // scroll (wheel hadn't finished flipping `shouldAutoFollow` yet).
    const recentlyScrolled = performance.now() - lastUserScrollAt.current < 400;

    if (shouldAutoFollow.current && !recentlyScrolled && (isNew || isGrowing)) {
      // Scroll only the rpPanel scroller, not all ancestors — `scrollIntoView`
      // bubbles up and would also scroll the page itself, fighting the user.
      const scroller = getScroller();
      if (scroller) {
        scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
      }
    }
    prevCount.current = count;
    prevLastText.current = lastText;
  }, [followUps.length, followUps.at(-1)?.text, followUps.at(-1)?.role, responding]);

  // Keep the anchor mounted even with no follow-ups — the wheel/touchmove
  // listener in the effect above relies on walking up from this ref to find
  // the rpPanel scroller. Without this, the listener never attaches when the
  // chat starts empty, and the user can't scroll up while the assistant types.
  if (followUps.length === 0) return <div ref={scrollAnchorRef} aria-hidden />;

  // The streaming assistant placeholder is always the newest assistant row
  // in the follow-up slice; we style its empty state as a typing indicator.
  let lastAssistantId: string | null = null;
  for (let i = followUps.length - 1; i >= 0; i--) {
    if (followUps[i]!.role === "assistant") {
      lastAssistantId = followUps[i]!.id;
      break;
    }
  }

  return (
    <div className="rp-chat">
      {followUps.map((msg) => {
        if (msg.role === "user") {
          return (
            <div key={msg.id} className="rp-chat-row rp-chat-row--user">
              <div className={clsx("rp-chat-bubble rp-chat-bubble--user", msg.queued && "rp-chat-bubble--queued")}>
                {msg.text}
              </div>
              {msg.queued ? (
                <Text variant="bodyXs" tone="muted" className="rp-chat-queued-label">
                  Queued — sends when the current reply finishes
                </Text>
              ) : null}
            </div>
          );
        }
        const isStreaming = responding && msg.id === lastAssistantId;
        const isLastAssistant = msg.id === lastAssistantId;
        const empty = !msg.text.trim();
        return (
          <div key={msg.id} className="rp-chat-row rp-chat-row--assistant">
            {isStreaming && empty ? (
              <TypingIndicator />
            ) : (
              <AssistantContent
                text={msg.text}
                isStreaming={isStreaming}
                onRetry={isLastAssistant && !responding ? onRetry : null}
                onSendMessage={onSendMessage}
              />
            )}
          </div>
        );
      })}
      <div ref={scrollAnchorRef} />
    </div>
  );
}

function AssistantContent({
  text,
  isStreaming,
  onRetry,
  onSendMessage,
}: {
  text: string;
  isStreaming: boolean;
  onRetry?: (() => void) | null;
  onSendMessage?: (text: string, productId?: number) => void;
}) {
  const parsed = parseChatResponse(text, isStreaming);
  const isError = text.startsWith("Sorry —") || text.includes("Please try again.");
  // Prose blocks stay inside the 80%-capped bubble (chat conversation feel);
  // widget blocks break out to the full chat row width so the 3-column layout
  // aligns with the right edge of the user's messages.
  return (
    <div className="rp-chat-stack">
      {parsed.blocks.map((block, i) =>
        block.kind === "text" ? (
          <div key={i} className="rp-chat-bubble rp-chat-bubble--assistant">
            <ChatMarkdown text={block.content} />
          </div>
        ) : (
          <div key={i} className="rp-widget-row">
            <WidgetRenderer widgetType={block.widgetType} data={block.data} onSendMessage={onSendMessage} />
          </div>
        ),
      )}
      {onRetry && isError ? (
        <div className="rp-chat-bubble rp-chat-bubble--assistant">
          <Button variant="ghost" leftIcon={<IconRefresh size={16} />} onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}
